import { takeLatest, take, put, race, call, delay } from 'redux-saga/effects';

import { FormControlState } from 'ia-react-core/~/interfaces/FormState';
import { DeepPartial } from 'ia-react-core/~/interfaces/DeepPartial';
import RequestResourceFailureById from 'ia-react-core/~/actions/resource/RequestResourceFailureById';
import RequestResourceSuccessById from 'ia-react-core/~/actions/resource/RequestResourceSuccessById';
import { ClientErrors } from 'ia-react-core/~/interfaces/ClientErrors';
import { AxiosResponse } from 'axios';
import scrollIntoView from '../../../utils/dom/scrollIntoView';
import openModalByName from '../../../actions/modal-fn/openModalByName';
import submitFormAcceptedById from '../../../actions/form-fn/submitFormAcceptedById';
import completeSubmitFormById from '../../../actions/form-fn/completeSubmitFormById';
import updateFormById from '../../../actions/form-fn/updateFormById';
import requestResourceById from '../../../actions/resource-fn/requestResourceById';
import requestResourceSuccessById from '../../../actions/resource-fn/requestResourceSuccessById';
import requestResourceFailureById from '../../../actions/resource-fn/requestResourceFailureById';
import resetFormById from '../../../actions/form-fn/resetFormById';
import { CreateFormProps } from './CreateForm';

type Props = CreateFormProps<any, any, {}> & {
  formName: string;
  formId: number;
  createResourceId: number;
  modalName: string;
  root: Document | ShadowRoot;
  onSuccess?: (data: AxiosResponse<any>) => void;
  onComplete?: () => void;
};

function* createForm(argsGetter: () => Props): any {
  const props = argsGetter();
  const {
    createResourceId, formId, validationFailureStatuses, modalName,
    controlErrorsSelector, formErrorsSelector, controlWarningsSelector, formWarningsSelector, onSuccess, onComplete,
  } = props;

  const noClientErrors: DeepPartial<FormControlState> = {
    controlErrors: {},
    formErrors: {},
    formWarnings: {},
    controlWarnings: {},
  };

  yield put(updateFormById(formId, { clientErrors: noClientErrors }, null, {
    mergeBlacklist: [noClientErrors],
  }));

  yield put(requestResourceById(createResourceId));

  const { failure, success }: {
    success: RequestResourceSuccessById;
    failure: RequestResourceFailureById;
  } = yield race({
    success: take(`${requestResourceSuccessById.type}/${createResourceId}`),
    failure: take(`${requestResourceFailureById.type}/${createResourceId}`),
  });

  const error = failure?.error;

  const response = (error && 'response' in error) ? error.response : null;

  yield put(completeSubmitFormById(formId, error));

  if (!failure) {
    yield put(resetFormById(formId, { resetValues: false }));
  }

  const controlErrorsDataSelector = controlErrorsSelector ?
    (r: unknown) => controlErrorsSelector(r) : (r: { data: { controlErrors: ClientErrors<any> } }) => r?.data?.controlErrors;

  const controlWarningsDataSelector = controlWarningsSelector ?
    (r: unknown) => controlWarningsSelector(r) : (r: { data: { controlWarnings: ClientErrors<any> } }) => r?.data?.controlWarnings;

  const controlErrors: ClientErrors<any> = validationFailureStatuses.includes(response?.status) ?
    controlErrorsDataSelector(response) :
    {};

  const controlWarnings: ClientErrors<any> = controlWarningsDataSelector(response);

  const formErrorsDataSelector = formErrorsSelector ?
    (r: unknown) => formErrorsSelector(r) : (r: { data: { formErrors: ClientErrors<any> } }) => r?.data?.formErrors;

  const formWarningsDataSelector = formWarningsSelector ?
    (r: unknown) => formWarningsSelector(r) : (r: { data: { formWarnings: ClientErrors<any> } }) => r?.data?.formWarnings;

  const formErrors: ClientErrors<any> = validationFailureStatuses.includes(response?.status) ?
    formErrorsDataSelector(response) :
    {};

  const formWarnings: ClientErrors<any> = formWarningsDataSelector(response);

  const clientErrors = { controlErrors, formErrors, controlWarnings, formWarnings };

  yield put(updateFormById(formId, { clientErrors }, null, {
    mergeBlacklist: [clientErrors],
  }));

  const isUnexpectedError = error && !validationFailureStatuses.includes(response?.status);

  if (isUnexpectedError) {
    yield put(openModalByName(modalName));
  } else {
    const target = getScrollingTarget(props);
    if (target) {
      yield delay(200);
      yield call(scrollIntoView, target);
    }
  }

  yield delay(200);

  if (!failure && onSuccess) {
    yield call(onSuccess, success.request as unknown as AxiosResponse);
  }

  if (onComplete) {
    yield call(onComplete);
  }
}

function getScrollingTarget({ formName, root }: Props): HTMLElement {
  const formElement: HTMLElement = root.querySelector(`[data-testid="Form"][name="${formName}"]`);
  const formMessage: HTMLElement = formElement?.querySelector('[data-form-message]');

  if (formMessage) return formMessage;

  const firstInvalidControl: HTMLElement = formElement.querySelector('[aria-errormessage][aria-invalid="true"]');

  if (firstInvalidControl) {
    const label: HTMLElement = firstInvalidControl?.closest('[data-testid="FormColLabel"]');
    return label || firstInvalidControl;
  }

  return null;
}

export default function* createFormSaga(argsGetter: () => Props) {
  const { formId } = argsGetter();

  yield takeLatest(`${submitFormAcceptedById.type}/${formId}`, createForm, argsGetter);
}
